enum Status {
  scheduled = 'scheduled',
  busy = 'busy',
  complete = 'complete',
  retry = 'retry',
  failed = 'failed',
}

/**
 * A helper class to store information about the current polling state to
 * import students. We need this information to decide whether or not we want to continue executing
 * the polling request.
 *
 * @class PollingStatus
 */
export default class PollingStatus {
  status: Status | null

  jobId: string

  /**
   * Constructor defines the valid states and checks if the passed in status. If an invalid status
   * is passed in, we will throw and ImportPollingStatusError which will be caught by our error handling
   * and reported to log service. The polling will be interrupted and shows an error message to the user. Its is important
   * for the status codes to be correct as we are making workflow decisions on them.
   *
   * @param {string} status
   * @param {string} jobId
   */
  constructor(status: string | null, jobId: string) {
    // Check if an unknown status was passed in from the backend. We also also allow null as a valid value
    // as it would indicate that a polling request has failed due to the absence of the status property
    if (!this.isStatus(status) && status !== null) {
      throw new Error(
        `Status "${String(
          status,
        )}" is not allowed, make sure the gravity does not return anything other than ${Object.values(Status).join(
          ',',
        )}`,
      )
    }
    this.status = status
    this.jobId = jobId
  }

  /**
   * Alternante constructor
   * @param status
   * @param jobId
   * @returns {PollingStatus}
   */
  static create(status: string | null, jobId: string) {
    return new PollingStatus(status, jobId)
  }

  isStatus(status: unknown): status is Status {
    return typeof status === 'string' && status in Status
  }

  /**
   * @returns {boolean}
   */
  isComplete() {
    return this.status === Status.complete
  }

  /**
   * Either null or a failed are valid states for a failed polling request. We also taking into account
   * @returns {boolean}
   */
  hasFailed() {
    return this.status === Status.failed || this.status === null
  }

  /**
   * Busy, scheduled and retry indicate if polling is still required
   * @returns {boolean}
   */
  isStillRunning() {
    return this.status !== null && [Status.scheduled, Status.busy, Status.retry].includes(this.status)
  }
}
